/**
 * Keyboard Shortcuts Dialog
 * 
 * Shows all available keyboard shortcuts for transaction management
 */

import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Keyboard, 
  MousePointer, 
  ArrowUp, 
  ArrowDown, 
  Check, 
  X,
  Edit3,
  Save,
  RotateCcw
} from 'lucide-react';

interface KeyboardShortcutsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface Shortcut {
  keys: string[];
  description: string;
  category: 'navigation' | 'selection' | 'editing' | 'actions';
  icon?: React.ReactNode;
}

const shortcuts: Shortcut[] = [
  // Navigation
  {
    keys: ['↑', '↓'],
    description: 'Navigate between transactions',
    category: 'navigation',
    icon: <ArrowUp className="h-4 w-4" />
  },
  {
    keys: ['Enter'],
    description: 'Open transaction details',
    category: 'navigation',
    icon: <MousePointer className="h-4 w-4" />
  },
  {
    keys: ['Escape'],
    description: 'Cancel selection or close dialogs',
    category: 'navigation',
    icon: <X className="h-4 w-4" />
  },
  {
    keys: ['F1'],
    description: 'Show keyboard shortcuts help',
    category: 'navigation',
    icon: <Keyboard className="h-4 w-4" />
  },
  
  // Selection
  {
    keys: ['Ctrl', 'A'],
    description: 'Select all visible transactions',
    category: 'selection',
    icon: <Check className="h-4 w-4" />
  },
  {
    keys: ['Ctrl', 'D'],
    description: 'Deselect all transactions',
    category: 'selection',
    icon: <X className="h-4 w-4" />
  },
  {
    keys: ['Space'],
    description: 'Toggle selection of focused transaction',
    category: 'selection',
    icon: <MousePointer className="h-4 w-4" />
  },
  
  // Editing
  {
    keys: ['Ctrl', 'E'],
    description: 'Edit focused transaction',
    category: 'editing',
    icon: <Edit3 className="h-4 w-4" />
  },
  {
    keys: ['Double Click'],
    description: 'Start editing transaction',
    category: 'editing',
    icon: <Edit3 className="h-4 w-4" />
  },
  {
    keys: ['Enter'],
    description: 'Save current edit',
    category: 'editing',
    icon: <Save className="h-4 w-4" />
  },
  {
    keys: ['Escape'],
    description: 'Cancel current edit',
    category: 'editing',
    icon: <RotateCcw className="h-4 w-4" />
  },
];

const categoryLabels = {
  navigation: 'Navigation',
  selection: 'Auswahl',
  editing: 'Bearbeitung',
  actions: 'Aktionen'
};

const categoryColors = {
  navigation: 'bg-blue-100 text-blue-800',
  selection: 'bg-green-100 text-green-800',
  editing: 'bg-purple-100 text-purple-800',
  actions: 'bg-orange-100 text-orange-800'
};

export const KeyboardShortcutsDialog = ({ open, onOpenChange }: KeyboardShortcutsDialogProps) => {
  const [searchTerm, setSearchTerm] = useState('');

  // Close dialog on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        onOpenChange(false);
      }
    };

    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [open, onOpenChange]);

  const filteredShortcuts = shortcuts.filter(shortcut =>
    shortcut.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    shortcut.keys.some(key => key.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const groupedShortcuts = filteredShortcuts.reduce((acc, shortcut) => {
    if (!acc[shortcut.category]) {
      acc[shortcut.category] = [];
    }
    acc[shortcut.category].push(shortcut);
    return acc;
  }, {} as Record<string, Shortcut[]>);

  const renderKey = (key: string) => {
    if (key === 'Ctrl') {
      return (
        <kbd className="px-2 py-1 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded-md">
          Ctrl
        </kbd>
      );
    }
    if (key === 'Shift') {
      return (
        <kbd className="px-2 py-1 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded-md">
          Shift
        </kbd>
      );
    }
    if (key === 'Alt') {
      return (
        <kbd className="px-2 py-1 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded-md">
          Alt
        </kbd>
      );
    }
    if (key === 'Double Click') {
      return (
        <kbd className="px-2 py-1 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded-md">
          Doppelklick
        </kbd>
      );
    }
    return (
      <kbd className="px-2 py-1 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded-md">
        {key}
      </kbd>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Keyboard className="h-5 w-5" />
            Tastenkürzel für Transaktionen
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Search */}
          <div className="relative">
            <input
              type="text"
              placeholder="Tastenkürzel suchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <Keyboard className="h-4 w-4 text-gray-400 absolute right-3 top-1/2 transform -translate-y-1/2" />
          </div>

          {/* Shortcuts by Category */}
          {Object.entries(groupedShortcuts).map(([category, categoryShortcuts]) => (
            <div key={category} className="space-y-3">
              <div className="flex items-center gap-2">
                <Badge className={categoryColors[category as keyof typeof categoryColors]}>
                  {categoryLabels[category as keyof typeof categoryLabels]}
                </Badge>
                <span className="text-sm text-gray-600">
                  {categoryShortcuts.length} Tastenkürzel
                </span>
              </div>
              
              <div className="grid gap-2">
                {categoryShortcuts.map((shortcut, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      {shortcut.icon && (
                        <div className="text-gray-600">
                          {shortcut.icon}
                        </div>
                      )}
                      <span className="text-sm font-medium text-gray-900">
                        {shortcut.description}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-1">
                      {shortcut.keys.map((key, keyIndex) => (
                        <div key={keyIndex} className="flex items-center gap-1">
                          {renderKey(key)}
                          {keyIndex < shortcut.keys.length - 1 && (
                            <span className="text-gray-400 text-xs">+</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {filteredShortcuts.length === 0 && searchTerm && (
            <div className="text-center py-8 text-gray-500">
              <Keyboard className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>Keine Tastenkürzel gefunden für "{searchTerm}"</p>
            </div>
          )}

          {/* Tips */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-medium text-blue-900 mb-2">💡 Tipps</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Verwenden Sie die Pfeiltasten für schnelle Navigation</li>
              <li>• Ctrl+A wählt alle sichtbaren Transaktionen aus</li>
              <li>• Doppelklick startet die Bearbeitung einer Transaktion</li>
              <li>• F1 oder Ctrl+? öffnet diese Hilfe</li>
              <li>• Escape bricht alle Aktionen ab</li>
            </ul>
          </div>
        </div>

        <div className="flex justify-end pt-4 border-t">
          <Button onClick={() => onOpenChange(false)}>
            Schließen
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
